"use client";

import React, { useState, useRef } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2, Film, Clock, CheckCircle2 } from "lucide-react";
import { StatusBadge } from "./technical-components";

export function VideoShowcaseSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleFullscreen = () => {
    if (!videoRef.current) return;
    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    }
  };

  const chapters = [
    { time: "0:00", title: "THE SPEED & STAMINA VOID", act: "ACT I: PROBLEM" },
    { time: "0:30", title: "THE OLYMPIO GENESIS & PORTAL", act: "ACT II: VISION" },
    { time: "1:00", title: "SCHOOL ADMINISTRATIVE TRANSFORMATION", act: "ACT III: ERP" },
    { time: "1:40", title: "BIOMETRIC GROWTH & TALENT RADAR", act: "ACT IV: SCIENCE" },
    { time: "2:20", title: "CAMPUS FACILITY MONETIZATION", act: "ACT V: REVENUE" },
  ];

  return (
    <section id="video" className="py-24 border-b border-[#3A3A38]/20 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <StatusBadge text="04. CINEMATIC PRODUCT FILM &amp; PLATFORM DEMO" />
          <h2 className="font-space font-bold text-4xl sm:text-6xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
            WATCH THE 3-MINUTE PLATFORM DEMO.
          </h2>
          <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
            SEE HOW OLYMPIO DIGITIZES PHYSICAL EDUCATION, POWERS SCHOOL ATHLETIC OPERATIONS, AND UNLOCKS HIDDEN OLYMPIC POTENTIAL.
          </p>
        </div>

        {/* Video Wireframe Player Container */}
        <div className="max-w-5xl mx-auto">
          <div className="relative border border-[#3A3A38]/30 bg-[#F7F7F5] p-3 rounded-none">
            {/* L-Shaped Corner Markers */}
            <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 border-[#1A3C2B]" />
            <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 border-[#1A3C2B]" />
            <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 border-[#1A3C2B]" />
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 border-[#1A3C2B]" />

            {/* Wireframe Header */}
            <div className="border-b border-[#3A3A38]/20 pb-2 mb-3 flex items-center justify-between font-mono-tech text-[10px] uppercase text-[#1A3C2B]">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#1A3C2B]" />
                <span>FINAL_PRODUCT_INTRO.MP4 // MASTER DEMO FILM</span>
              </div>
              <span>RESOLUTION: 1080P // 60 FPS</span>
            </div>

            {/* Video Canvas */}
            <div className="relative aspect-video bg-slate-950 border border-[#3A3A38]/20 overflow-hidden group">
              <video
                ref={videoRef}
                src="/media/final.mp4"
                poster="/media/olympio_logo.jpeg.png"
                playsInline
                preload="none"
                className="w-full h-full object-cover"
                onEnded={() => setIsPlaying(false)}
              />

              {/* Play Overlay Button */}
              {!isPlaying && (
                <div className="absolute inset-0 bg-[#1A3C2B]/30 backdrop-blur-xs flex items-center justify-center">
                  <button
                    onClick={togglePlay}
                    className="w-16 h-16 bg-[#1A3C2B] text-white border border-white/30 flex items-center justify-center hover:bg-[#132d20] hover:scale-105 transition rounded-none font-mono-tech"
                  >
                    <Play className="w-6 h-6 fill-white ml-1 text-white" />
                  </button>
                </div>
              )}

              {/* Bottom Controls Bar */}
              <div className="absolute bottom-0 inset-x-0 p-3 bg-[#1A3C2B]/90 backdrop-blur-md text-white font-mono-tech text-[10px] flex items-center justify-between opacity-90 group-hover:opacity-100 transition">
                <div className="flex items-center gap-3">
                  <button onClick={togglePlay} className="hover:text-[#9EFFBF] flex items-center gap-1">
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-current" />}
                    <span>{isPlaying ? "PAUSE" : "PLAY"}</span>
                  </button>
                  <button onClick={toggleMute} className="hover:text-[#9EFFBF]">
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>

                <div className="flex items-center gap-4">
                  <span className="text-[#9EFFBF]">OLYMPIO ERP MASTER DEMO</span>
                  <button onClick={handleFullscreen} className="hover:text-[#9EFFBF]">
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Chapters Grid below video */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-[1px] bg-[#3A3A38]/20 border-t border-[#3A3A38]/20 mt-3 pt-2 font-mono-tech text-[10px]">
              {chapters.map((ch, idx) => (
                <div key={idx} className="bg-[#F7F7F5] p-2 space-y-0.5">
                  <div className="text-[#1A3C2B] font-bold">{ch.time} // {ch.act}</div>
                  <div className="text-[#3A3A38]/70 truncate uppercase">{ch.title}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
